import React, { useMemo } from 'react';
import { FaCalendarAlt, FaRegCreditCard } from 'react-icons/fa';
import PDFHeader from './PDFHeader';
import {
  buildPaymentCalendar,
  formatPaymentDate,
  formatPaymentDateTime,
} from './paymentCalendar';

interface PDFPage2PaymentCalendarProps {
  createdAt?: string;
  tripStartDate?: string;
  flightCost: number;
  hotelCost: number;
  cabCost: number;
  totalPackageCost: number;
  currency: 'INR' | 'USD';
  renderedAt?: string;
}

const CANCELLATION_POLICY = [
  'Flight cancellation charges apply as per airline fare rules.',
  'More than 30 days before travel: 15% of land-package cost.',
  '16–30 days before travel: 30% of land-package cost.',
  '8–15 days before travel: 50% of land-package cost.',
  'Within 7 days or no-show: 100% cancellation charges.',
  'Non-refundable flights, hotels or activities will be charged at actual cost.',
  'Approved refunds will be processed after receipt from the relevant supplier.',
];

const formatMoney = (amount: number, currency: 'INR' | 'USD') => {
  const symbol = currency === 'USD' ? '$' : '₹';
  return `${symbol}${Number(amount || 0).toLocaleString('en-IN')}`;
};

const PDFPage2PaymentCalendar: React.FC<PDFPage2PaymentCalendarProps> = ({
  createdAt,
  tripStartDate,
  flightCost,
  hotelCost,
  cabCost,
  totalPackageCost,
  currency,
  renderedAt,
}) => {
  const calendar = useMemo(
    () =>
      buildPaymentCalendar({
        createdAt,
        tripStartDate,
        flightCost,
        hotelCost,
        cabCost,
        totalPackageCost,
      }),
    [cabCost, createdAt, flightCost, hotelCost, totalPackageCost, tripStartDate]
  );
  const CalendarIcon = FaCalendarAlt as unknown as React.FC<any>;
  const PaymentIcon = FaRegCreditCard as unknown as React.FC<any>;

  return (
    <div className="pdf-page">
      <PDFHeader renderedAt={renderedAt} />
      <section className="pdf-section">
        <div className="payment-calendar-card pdf-keep-together">
          <div className="payment-calendar-title">
            <CalendarIcon aria-hidden />
            <h3>Payment Calendar</h3>
          </div>

          {calendar.kind === 'error' ? (
            <div className="payment-calendar-error">{calendar.message}</div>
          ) : (
            <>
              <div className="payment-calendar-list">
                {calendar.milestones.map((milestone) => (
                  <div className="payment-calendar-item" key={`${milestone.title}-${milestone.step}`}>
                    <div className="payment-calendar-step">{milestone.step}</div>
                    <div className="payment-calendar-body">
                      <div className="payment-calendar-row">
                        <div className="payment-calendar-label">
                          <PaymentIcon aria-hidden />
                          <span>{milestone.title}</span>
                        </div>
                        <div className="payment-calendar-amount">
                          {formatMoney(milestone.amount, currency)}
                        </div>
                      </div>
                      <div className="payment-calendar-due">
                        Due by{' '}
                        {milestone.dueFormat === 'dateTime'
                          ? formatPaymentDateTime(milestone.dueAt)
                          : formatPaymentDate(milestone.dueAt)}
                      </div>
                      <div className="payment-calendar-note">{milestone.note}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="payment-calendar-policy">
                <h4>Cancellation Policy</h4>
                <ul>
                  {CANCELLATION_POLICY.map((item, index) => (
                    <li key={`${item}-${index}`}>{item}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default PDFPage2PaymentCalendar;
